import { Check, ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { HexColor } from '@nx-smartmonkey/shared/domain';
import { parseHMtoSeconds, parseSecondsToHM } from '@nx-smartmonkey/shared/helpers';
import {
  HSpacer,
  RoutalInputPhone,
  RoutalInputPhoneValue,
  RoutalPalette,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
  VSpacer,
} from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import Trash from '../../assets/svg/onboarding/icons/Trash';
import { Organization } from '../../domain/organization/Organization';
import { Project } from '../../domain/project/Project';
import { Vehicle, WeekDay, weekDays } from '../../domain/vehicle/Vehicle';
import { useRetrieveSessionCountry } from '../../hooks/session/useRetrieveSessionCountry';
import { VehicleAvatar } from '../../pages/shared/VehicleAvatar';
import InputText from '../../react-components/InputText';
import { InputTimePicker } from '../../react-components/InputTimePicker';
import Text from '../../react-components/Text';
import { CreateVehiclesConstraints } from './create_vehicles/CreateVehicles';
import { LocationInputsProps, VehiclesLocationInputs } from './vehicles_location/VehiclesLocationInputs';

interface NewVehiclePillProps {
  project: Project;
  organization: Organization;
  vehicle: Vehicle;
  constraints: CreateVehiclesConstraints;
  canRemove?: boolean;
  onUpdate: (vehicle: Vehicle) => void;
  onRemove: (vehicle: Vehicle) => void;
}

export const NewVehiclePill = ({
  project,
  organization,
  vehicle,
  constraints,
  canRemove,
  onUpdate,
  onRemove,
}: NewVehiclePillProps) => {
  const intl = useIntl();
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [constraintsVisible, setConstraintsVisible] = useState<boolean>(false);
  const { countryCode } = useRetrieveSessionCountry();

  // Set isActive to false when user clicks outside the pill
  useEffect(() => {
    const handleClickOutside = () => {
      setIsActive(false);
    };

    if (isActive) {
      document.addEventListener(`mousedown`, handleClickOutside);
    } else {
      document.removeEventListener(`mousedown`, handleClickOutside);
    }

    return () => {
      document.removeEventListener(`mousedown`, handleClickOutside);
    };
  }, [isActive]);

  const handleUpdateDays: (val: WeekDay[]) => void = (val: WeekDay[]) => {
    const orderedDays = val.sort((a, b) => weekDays.indexOf(a) - weekDays.indexOf(b));
    vehicle.default_working_days = orderedDays;
    onUpdate(vehicle);
  };
  const formatWeakDays = () => {
    if (vehicle.default_working_days?.length === 7) {
      return intl.formatMessage({ id: `vehicles.working_days.every_day` });
    }
    return (vehicle.default_working_days ?? [])
      .map((day, index) => {
        return `${intl.formatMessage({ id: `${day}.short` })}${
          index < (vehicle.default_working_days ?? []).length - 1 ? `, ` : ``
        }`;
      })
      .join(``);
  };

  return (
    <div
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
      style={{
        position: `relative`,
        paddingRight: `40px`,
      }}
    >
      <Grid
        container
        direction="column"
        wrap="nowrap"
        key={vehicle.id}
        onClick={() => {
          setIsActive(!isActive);
        }}
        style={{
          border: `1px solid ${isActive ? vehicle.color!.value : RoutalPalette.neutral10}`,
          borderRadius: `10px`,
          backgroundColor: RoutalPalette.neutral00,
        }}
      >
        <Grid
          container
          direction="column"
          wrap="nowrap"
          style={{
            padding: `20px`,
            paddingRight: `30px`,
            borderBottom: `1px solid ${RoutalPalette.neutral10}`,
          }}
        >
          <Grid item container direction="row" wrap="nowrap">
            <Grid item>
              <VehicleAvatar
                vehicleId={vehicle.id}
                vehicleColor={vehicle.color?.value}
                withColorPicker
                onUpdate={(color: HexColor) => {
                  vehicle.color = color;
                  onUpdate(vehicle);
                }}
              />
            </Grid>

            <HSpacer medium />

            <Grid item style={{ width: `100%` }}>
              <InputText
                autoFocus
                fieldValue={vehicle.label}
                placeholderIntlId="company.onboarding.vehicles.create.name.ph"
                inputContainerStyles={{ width: `100%` }}
                focusedBorderColor={vehicle.color?.value}
                onChange={(text: any) => {
                  vehicle.label = text;
                  onUpdate(vehicle);
                }}
                required
                validators={[
                  {
                    errorMessage: (
                      <Text
                        tagName="span"
                        style={{ color: RoutalPalette.error.medium }}
                        intlId="validation.mandatory_field"
                      />
                    ),
                    isInvalid: (text?: string) => (text ?? ``).length === 0,
                  },
                ]}
              />
            </Grid>

            <HSpacer medium />

            <Grid item style={{ width: `100%` }}>
              <RoutalInputPhone
                id={`input-phone-${vehicle.id}`}
                countryCallingCodeEditable={false}
                focusInputOnCountrySelection
                international
                style={{
                  width: `100%`,
                }}
                numberInputProps={{
                  autoComplete: `off`,
                  required: true,
                  name: `phoneNumber`,
                  style: {
                    width: `100%`,
                  },
                }}
                inputFocusBorderColor={vehicle.color?.value}
                placeholder={intl.formatMessage({ id: `company.onboarding.vehicles.create.phone.ph` })}
                defaultCountry={organization?.country_code ?? countryCode}
                value={(vehicle.phone ?? ``) as RoutalInputPhoneValue}
                onChange={(value?: RoutalInputPhoneValue) => {
                  vehicle.phone = value;
                  onUpdate(vehicle ?? ``);
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        {constraintsVisible ? (
          <Grid
            container
            direction="column"
            alignItems="center"
            style={{
              padding: `16px 20px`,
              borderBottom: `1px solid ${RoutalPalette.neutral10}`,
            }}
          >
            <Grid item container direction="row" wrap="nowrap" style={{ width: `100%` }}>
              <Grid item container direction="row" alignItems="center" wrap="nowrap" style={{ width: `100%` }}>
                <Grid item>
                  <Text variant="label" intlId="from" />
                </Grid>

                <HSpacer small />

                <Grid item style={{ width: `100%` }}>
                  <InputTimePicker
                    inputStyles={{
                      marginTop: 0,
                      backgroundColor: `transparent`,
                    }}
                    inputFocusBorderColor={vehicle.color?.value}
                    timeValue={vehicle.default_time_window ? parseSecondsToHM(vehicle.default_time_window[0]) : `00:00`}
                    onChange={(time: string) => {
                      vehicle.default_time_window = [parseHMtoSeconds(time), vehicle.default_time_window![1]];
                      onUpdate(vehicle);
                    }}
                  />
                </Grid>
              </Grid>

              <HSpacer medium />

              <Grid item container direction="row" alignItems="center" wrap="nowrap" style={{ width: `100%` }}>
                <Grid item>
                  <Text variant="label" intlId="to" style={{ textTransform: `lowercase` }} />
                </Grid>

                <HSpacer small />

                <Grid item style={{ width: `100%` }}>
                  <InputTimePicker
                    inputStyles={{
                      marginTop: 0,
                      backgroundColor: `transparent`,
                    }}
                    inputFocusBorderColor={vehicle.color?.value}
                    timeValue={vehicle.default_time_window ? parseSecondsToHM(vehicle.default_time_window[1]) : `00:00`}
                    onChange={(time: string) => {
                      vehicle.default_time_window = [vehicle.default_time_window![0], parseHMtoSeconds(time)];
                      onUpdate(vehicle);
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>

            <VSpacer medium />

            <Grid item container>
              <VehiclesLocationInputs
                focusedBorderColor={vehicle.color?.value}
                vehiclesLocation={{
                  start: vehicle.default_start_location ?? undefined,
                  end: vehicle.default_end_location ?? undefined,
                }}
                onChange={(vehiclesLocation: LocationInputsProps) => {
                  vehicle.default_start_location = vehiclesLocation.start;
                  vehicle.default_end_location = vehiclesLocation.end;
                  onUpdate(vehicle);
                }}
              />
            </Grid>

            {(constraints.volume || constraints.weight) && (
              <>
                <VSpacer medium />
                <Grid item container direction="row" style={{ width: `100%`, display: `flex`, flexWrap: `nowrap` }}>
                  {constraints.weight && (
                    <Grid item container direction="row" wrap="nowrap" alignItems="center" style={{ width: `50%` }}>
                      <Grid item>
                        <Text variant="label" intlId="company.onboarding.vehicles.create.weight" />
                      </Grid>

                      <HSpacer small />

                      <Grid item style={{ width: `100%` }}>
                        <InputText
                          fieldValue={vehicle.default_max_weight}
                          placeholderIntlId="none"
                          focusedBorderColor={vehicle.color?.value}
                          inputContainerStyles={{ width: `100%` }}
                          onChange={(text: any) => {
                            vehicle.default_max_weight = text;
                            onUpdate(vehicle);
                          }}
                          endAdornment={<Text style={{ marginRight: `8px` }}>{project?.units?.weight}</Text>}
                        />
                      </Grid>
                    </Grid>
                  )}

                  {constraints.volume && (
                    <>
                      {constraints.weight && <HSpacer small />}

                      <Grid item container direction="row" wrap="nowrap" alignItems="center" style={{ width: `50%` }}>
                        <Grid item>
                          <Text variant="label" intlId="company.onboarding.vehicles.create.volume" />
                        </Grid>

                        <HSpacer small />

                        <Grid item style={{ width: `100%` }}>
                          <InputText
                            fieldValue={vehicle.default_max_volume}
                            placeholderIntlId="none"
                            focusedBorderColor={vehicle.color?.value}
                            inputContainerStyles={{ width: `100%` }}
                            onChange={(text: any) => {
                              vehicle.default_max_volume = text;
                              onUpdate(vehicle);
                            }}
                            endAdornment={<Text style={{ marginRight: `8px` }}>{project?.units?.volume}</Text>}
                          />
                        </Grid>
                      </Grid>
                      {constraints.weight && <HSpacer small />}
                    </>
                  )}
                </Grid>
              </>
            )}
            <VSpacer medium />
            <Grid style={{ width: `100%` }}>
              <Text
                intlId="vehicles.data.details.labour_day"
                variant="label"
                style={{ cursor: `default`, marginBottom: `8px` }}
              />
              <RoutalSelect
                id={`working-days-popover`}
                containerStyle={{
                  width: `100%`,
                  height: `28px`,
                  marginBottom: `8px`,
                }}
                justifyContent="space-between"
                popoverProps={{
                  maxHeight: `200px`,

                  options: weekDays.map((value: string, index: number): RoutalSelectMultiSelectOption => {
                    return {
                      key: `option-${index}`,
                      listItemStyles: {
                        whiteSpace: `normal`,
                        overflow: `auto`,
                      },
                      closeOnClick: false,
                      onClick: () => {
                        if (vehicle.default_working_days?.includes(value as WeekDay)) {
                          handleUpdateDays(vehicle.default_working_days?.filter((day) => day !== value));
                        } else {
                          handleUpdateDays([...(vehicle.default_working_days ?? []), value as WeekDay]);
                        }
                      },
                      content: (
                        <Grid style={{ display: `flex`, justifyContent: `space-between` }}>
                          <Text intlId={value} />
                          {vehicle.default_working_days?.includes(value as WeekDay) && (
                            <Check
                              style={{
                                color: RoutalPalette.success.medium,
                                fontSize: `24px`,
                              }}
                            />
                          )}
                        </Grid>
                      ),
                    };
                  }),
                }}
              >
                <Text
                  style={{
                    cursor: `pointer`,
                  }}
                >
                  {formatWeakDays()}
                </Text>
              </RoutalSelect>
            </Grid>
          </Grid>
        ) : null}

        <Grid
          container
          direction="row"
          alignItems="center"
          justifyContent="center"
          onClick={() => setConstraintsVisible(!constraintsVisible)}
          style={{
            padding: `4px`,
            backgroundColor: RoutalPalette.neutral00,
            borderRadius: `10px`,
          }}
        >
          <Grid item>
            <Text style={{ cursor: `pointer` }} intlId="company.onboarding.vehicles.create.constraints" />
          </Grid>

          <Grid item>
            {constraintsVisible ? (
              <ExpandLessIcon style={{ cursor: `pointer` }} width={12} height={12} />
            ) : (
              <ExpandMoreIcon style={{ cursor: `pointer` }} width={12} height={12} />
            )}
          </Grid>
        </Grid>
      </Grid>

      {canRemove && isHovered && (
        <div style={{ position: `absolute`, top: `calc(50% - 12px)`, right: `0px` }}>
          <Trash
            style={{
              cursor: `pointer`,
              width: `24px`,
              height: `24px`,
            }}
            color={RoutalPalette.error.medium}
            onClick={() => onRemove(vehicle)}
          />
        </div>
      )}
    </div>
  );
};
